import * as React from 'react';
import {classes, style} from 'typestyle';
import {
  betweenJustified,
  center,
  centerCenter,
  centerJustified,
  end,
  endJustified,
  flex1,
  horizontal,
  horizontallySpaced,
  vertical,
  verticallySpaced
} from 'csstips';
import {important, percent, px, rem, url} from 'csx';
import {SVGIcon} from './svg-icon';
import {ButtonCircle} from './button-circle';
import {colorBackground, colorBrand, colorSlate, colorSubtle} from '../theme/color';
import {ButtonPill} from './button-pill';
import {observer} from 'mobx-react';
import {mediaMobileOnly, mediaTablet} from '../theme/media';
import {Alert} from './alert';
import {transitionQuickEase} from '../theme/transition';
import {ArtistLinkList} from './artist-link-list';
import {resetAnchorStyles} from '../theme/reset';
import {ChannelSchema, LicenceSchema, TrackSchema} from '../types/schema';
import {getTrackImageURL} from '../lib/image';
import {ImagePreloader} from '../types/fn';
import {formatCurrency} from '../lib/currency';
import {WaveformImage} from './waveform-image';
import {ConnectedTrackVouchStatus} from './track/vouch-status';

export interface PageCheckoutDownloadTracksModel {
  tracks: Array<TrackSchema>;
  licences: Array<LicenceSchema>;
  currency: string;
  price: string | number;
  priceBrandSponsored: string | number;
  prices: Array<string | number>;
  brandedContentWarning: string;
  channel: ChannelSchema
}

export interface PageCheckoutDownloadTracksProps {
  preload: ImagePreloader;
  preloadWaveform: ImagePreloader;
  className?: string;
  model: PageCheckoutDownloadTracksModel;
  getPrice?(identity: string): number;

  onRemoveTrack?(identity: string): void;
  onClickLink(event: React.MouseEvent<any>): void;

  onDownload?(licenceId: string): void;
  onAttribution?(licenceId: string): void;

  collapsed: boolean;
  download?: boolean;
  PlayButton: any;
  BrandSponsoredLabel: any;
  i18n?: {
    title: string;
    remove: string;
    rights: string;
    download: string;
    attribution: string;
    eula: string;
    empty: string;
  }
}

export interface PageCheckoutDownloadTracksState {
  limitations: { [identity: string]: boolean }
}

@observer
export class PageCheckoutDownloadTracks extends React.Component<PageCheckoutDownloadTracksProps, PageCheckoutDownloadTracksState> {
  private container: HTMLDivElement;

  state = { limitations: {} };

  render() {
    const { preload, preloadWaveform, className, model, PlayButton, i18n = PageCheckoutDownloadTracks.i18n, onRemoveTrack, download, onDownload, onClickLink, BrandSponsoredLabel } = this.props;
    if (model.licences.length === 0) return (
      <div className={PageCheckoutDownloadTracks.styles.empty}>
        {i18n.empty}
      </div>
    );

    let seenFirstStandardLicense = false;

    return (
      <div className={className} ref={el => this.container = el}>
        {model.licences.map((licence, index) => {
          const track = licence.track;
          const price = model.channel
            ? typeof model.prices[index] !== 'undefined'
              ? model.prices[index]
              : model.price
            : licence.brand_sponsored
              ? model.priceBrandSponsored
              : model.price;

          let isFirstLicenseFree = false;

          if (!model.channel && !seenFirstStandardLicense && !licence.brand_sponsored)
            seenFirstStandardLicense = isFirstLicenseFree = true;

          return (
            <div key={index} className={PageCheckoutDownloadTracks.styles.track}>
              <div className={PageCheckoutDownloadTracks.styles.tile}
                   style={{ backgroundImage: url(getTrackImageURL(track, preload)) }}>
                <PlayButton track={track}/>
              </div>
              <div className={PageCheckoutDownloadTracks.styles.details}>
                <div className={PageCheckoutDownloadTracks.styles.header}>
                  <a
                    className={PageCheckoutDownloadTracks.styles.link}
                    href={`/music/artists/${track.artists[0].slug}/track/${track.slug}`}
                    onClick={onClickLink}
                  >{track.title}</a>
                  {licence.brand_sponsored && (
                    <BrandSponsoredLabel expanded={true}/>
                  )}
                </div>
                <ArtistLinkList
                  model={{ artists: track.artists }}
                  onClickLink={onClickLink}
                  className={PageCheckoutDownloadTracks.styles.linkArtist}
                  linkClassName={PageCheckoutDownloadTracks.styles.linkArtistItem}
                />
                <div className={PageCheckoutDownloadTracks.styles.waveformContainer}>
                  <WaveformImage
                    className={PageCheckoutDownloadTracks.styles.waveform}
                    identity={track.audio.identity}
                    preloadWaveform={preloadWaveform}
                  />
                  <div
                    className={PageCheckoutDownloadTracks.styles.price}>
                    {!model.channel && (
                      <div>from</div>
                    )}
                    {isFirstLicenseFree && (
                      <span>{formatCurrency(price, model.currency)}</span>
                    )}
                    {formatCurrency(isFirstLicenseFree ? 0 : price, model.currency)}
                  </div>
                </div>
                <div className={PageCheckoutDownloadTracks.styles.buttonsContainer}>
                  {onRemoveTrack && (
                    <div className={PageCheckoutDownloadTracks.styles.buttons} onClick={() => onRemoveTrack(track.identity)}>
                      <ButtonCircle size={30} className={PageCheckoutDownloadTracks.styles.button}>
                        <SVGIcon.Cross size={10}/>
                      </ButtonCircle>
                      <span>{i18n.remove}</span>
                    </div>
                  )}
                  {download && (
                    <div
                      className={PageCheckoutDownloadTracks.styles.downloadButtons}>
                      <ButtonPill
                        className={PageCheckoutDownloadTracks.styles.actionButton}
                        muted={true}
                        href={`/account/licenses`}
                        onClick={onClickLink}
                        children={i18n.eula}
                      />
                      {onDownload && (
                        <ButtonPill
                          className={PageCheckoutDownloadTracks.styles.actionButton}
                          primary={true}
                          onClick={onDownload.bind(null, this.getLicenceIdentity(track))}
                          children={i18n.download}
                        />
                      )}
                    </div>
                  )}
                  {!download && track.branded_content_allowed === 0 && (
                    <div className={classes(PageCheckoutDownloadTracks.styles.buttons, this.maybeHide())}>
                      <span>{i18n.rights}</span>
                      <ButtonCircle onClick={this.toggleLimitations.bind(this, track.identity)} size={30}
                                    className={classes(PageCheckoutDownloadTracks.styles.button, track.branded_content_allowed === 0 && PageCheckoutDownloadTracks.styles.buttonActive)}>
                        <SVGIcon.ArrowRight size={10}
                                            className={classes(PageCheckoutDownloadTracks.styles.chevron, this.state.limitations[ track.identity ] && PageCheckoutDownloadTracks.styles.chevronActive)}/>
                      </ButtonCircle>
                    </div>
                  )}
                  {!download && track.branded_content_allowed !== 0 && (
                    <div className={classes(PageCheckoutDownloadTracks.styles.buttons, this.maybeHide())}/>
                  )}
                </div>
                {!download && this.state.limitations[ track.identity ] && track.branded_content_allowed === 0 && (
                  <Alert type="simple"
                         title="Sponsored content not allowed"
                         onClose={this.toggleLimitations.bind(this, track.identity)}
                         position="top-right">
                    <p className={PageCheckoutDownloadTracks.styles.alertParagraph}>{model.brandedContentWarning}</p>
                  </Alert>
                )}
                <ConnectedTrackVouchStatus identity={track.identity} />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  componentWillMount() {
    this.prepareLimitations(this.props)
  }

  componentWillReceiveProps(nextProps) {
    this.prepareLimitations(nextProps);
  }

  prepareLimitations = (props: PageCheckoutDownloadTracksProps) => {
    const limitations = props.model.tracks.reduce((acc, track) => ({
      ...acc,
      [track.identity]: track.branded_content_allowed === 0
    }), {});

    this.setState({ limitations });
  };

  getLicenceIdentity = (track: TrackSchema) => {
    const licence = this.props.model.licences.find(licence => licence.track.identity === track.identity);
    return licence ? licence.identity : null
  };

  maybeHide = () => this.props.collapsed && PageCheckoutDownloadTracks.styles.hide;

  toggleLimitations = (trackIdentity: string) => {
    this.setState({
      limitations: {
        ...this.state.limitations,
        [trackIdentity]: !this.state.limitations[ trackIdentity ]
      }
    });
  };

  static i18n = {
    title: 'Your tracks',
    remove: 'Remove',
    rights: 'Restrictions',
    download: 'Download track',
    eula: 'View Licences',
    attribution: 'View video attribution',
    empty: 'Your basket is empty'
  };

  static styles = {
    empty: style({
      ...centerCenter,
      height: px(120),
      fontWeight: 200,
      fontSize: px(18),
      color: colorSubtle.toString()
    }),
    header: style({
      ...horizontal,
      ...betweenJustified,
    }),
    link: style(
      {
        color: important(colorSlate.toString()),
        flex: 1,
        textDecoration: 'underline',
        fontWeight: 700,
        fontSize: px(16),
        lineHeight: px(21),
        whiteSpace: 'pre',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        $nest: { ...resetAnchorStyles(colorBrand.toString()) }
      },
      mediaMobileOnly({
        padding: '0 0 0 65px'
      })
    ),
    linkArtist: style(
      {
        fontWeight: 400,
        fontSize: px(14),
        lineHeight: px(21),
        whiteSpace: 'pre',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      },
      mediaMobileOnly({
        padding: '0 0 0 65px'
      })
    ),
    linkArtistItem: style({
      fontSize: px(12),
      color: important(colorSubtle.toString()),
      $nest: { ...resetAnchorStyles(colorBrand.toString()) }
    }),
    track: style({
      ...horizontal,
      ...horizontallySpaced(15),
      position: 'relative',
      width: percent(100),
      borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
      padding: '15px 0',
      $nest: {
        '&:last-child': {
          borderBottom: 'none',
        }
      }
    }),
    tile: style(
      {
        ...horizontal,
        ...endJustified,
        ...end,
        flexShrink: 0,
        width: px(150),
        height: px(150),
        padding: px(5),
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      mediaMobileOnly({
        ...centerCenter,
        position: 'absolute',
        top: px(15),
        left: 0,
        width: px(50),
        height: px(50)
      })
    ),
    hide: style({
      display: important('none')
    }),
    details: style({
      ...flex1,
      ...vertical,
      padding: '5px 0',
      overflow: 'hidden'
    }),
    waveformContainer: style({
      ...flex1,
      ...horizontal
    }),
    waveform: style(
      {
        ...flex1,
        borderRadius: px(50),
        minHeight: px(48),
        maxHeight: px(48),
        margin: '10px 0',
        backgroundSize: '100% 100%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      mediaMobileOnly({
        minHeight: px(40)
      })
    ),
    price: style({
      ...vertical,
      ...centerJustified,
      ...end,
      position: 'relative',
      width: px(140),
      fontSize: px(30),
      $nest: {
        '& div': {
          display: 'block',
          fontSize: rem(0.75),
          height: rem(1.5),
          marginTop: rem(-1.25),
          color: colorSubtle.toString()
        },
        '& span': {
          display: 'block',
          textDecoration: 'line-through',
          fontSize: rem(1),
          height: rem(1.5),
        }
      }
    }),
    buttonsContainer: style({
      ...horizontal,
      ...betweenJustified,
      minHeight: px(30),
      $nest: {
        '&> div:last-child': {
          ...flex1,
          textAlign: 'right'
        }
      }
    }),
    buttons: style({
      ...horizontal,
      ...horizontallySpaced(15),
      ...endJustified,
      ...center
    }),
    button: style({
      backgroundColor: important(colorBackground.toString()),
      transition: `background-color ${transitionQuickEase}`,
      $nest: {
        '&:hover': {
          backgroundColor: important(colorBackground.darken(0.15).toString()),
        },
        '& g': {
          stroke: colorBrand.toString(),
        }
      }
    }),
    buttonActive: style({
      backgroundColor: important(colorBackground.toString()),
      $nest: {
        '& g': {
          stroke: colorBrand.toString(),
        },
      }
    }),
    downloadButtons: style(
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(15),
        ...endJustified,
        ...center
      }),
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(15)
      })
    ),
    actionButton: style(mediaMobileOnly({
      ...vertical,
      ...verticallySpaced(15)
    })),
    chevron: style({
      transform: 'rotate(-90deg)'
    }),
    chevronActive: style({
      transform: important('rotate(90deg)')
    }),
    shareURL: style({
      $nest: {
        '& > p': {
          margin: 0
        }
      }
    }),
    alertParagraph: style({
        margin: 0,
      }
    )
  }
}